import React from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { Refresh, Stop, Sync } from '@mui/icons-material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';

import { api } from '../../../services/api';

export const PlaybackSyncSessionsTab: React.FC = () => {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['playback-sync-sessions'],
    queryFn: () => api.getPlaybackSyncSessions({ page: 1, size: 100 }),
    refetchInterval: 10000,
  });

  const cancelMutation = useMutation({
    mutationFn: (id: number) => api.cancelPlaybackSyncSession(id),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['playback-sync-sessions'] });
      enqueueSnackbar('Sync session остановлена', { variant: 'success' });
    },
    onError: (error: any) => enqueueSnackbar(error?.detail || error?.message || 'Ошибка остановки sync session', { variant: 'error' }),
  });

  const resyncMutation = useMutation({
    mutationFn: (id: number) => api.resyncPlaybackSyncSession(id, { start_delay_seconds: 10 }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['playback-sync-sessions'] });
      enqueueSnackbar('Повторная синхронизация отправлена', { variant: 'success' });
    },
    onError: (error: any) => enqueueSnackbar(error?.detail || error?.message || 'Ошибка resync', { variant: 'error' }),
  });

  const sessions = data?.items || [];

  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        <Box>
          <Typography variant="h6">Sync Sessions</Typography>
          <Typography variant="body2" color="text.secondary">
            Синхронные запуски плейлистов, drift и состояние устройств.
          </Typography>
        </Box>
        <Button startIcon={<Refresh />} onClick={() => refetch()} disabled={isLoading}>
          Обновить
        </Button>
      </Stack>

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Session</TableCell>
              <TableCell>Плейлист</TableCell>
              <TableCell>Старт</TableCell>
              <TableCell>Статус</TableCell>
              <TableCell>Устройства</TableCell>
              <TableCell align="right">Действия</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading && (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <CircularProgress size={22} />
                </TableCell>
              </TableRow>
            )}
            {!isLoading && sessions.map((session) => {
              const counts = session.devices.reduce<Record<string, number>>((acc, row) => {
                acc[row.status] = (acc[row.status] || 0) + 1;
                return acc;
              }, {});
              return (
                <TableRow key={session.id}>
                  <TableCell>#{session.id}</TableCell>
                  <TableCell>{session.playlist_name || `Playlist #${session.playlist_id}`}</TableCell>
                  <TableCell>{new Date(session.start_at).toLocaleString()}</TableCell>
                  <TableCell>
                    <Chip size="small" label={session.status} color={session.status === 'active' ? 'success' : 'default'} />
                  </TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={0.5} flexWrap="wrap">
                      {Object.entries(counts).map(([status, count]) => (
                        <Chip key={status} size="small" variant="outlined" label={`${status}: ${count}`} />
                      ))}
                    </Stack>
                  </TableCell>
                  <TableCell align="right">
                    <Button
                      size="small"
                      startIcon={<Sync />}
                      onClick={() => resyncMutation.mutate(session.id)}
                      disabled={resyncMutation.isPending || session.status === 'cancelled'}
                    >
                      Resync
                    </Button>
                    <Button
                      size="small"
                      color="error"
                      startIcon={<Stop />}
                      onClick={() => cancelMutation.mutate(session.id)}
                      disabled={cancelMutation.isPending || ['cancelled', 'completed'].includes(session.status)}
                    >
                      Stop
                    </Button>
                  </TableCell>
                </TableRow>
              );
            })}
            {!isLoading && sessions.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  Sync sessions пока нет
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default PlaybackSyncSessionsTab;
